import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import '../components/CreateEditListing.css';

const CreateEditListing = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = Boolean(id);

    const [listing, setListing] = useState({
        address: '',
        city: '',
        state: '',
        zip: '',
        price: '',
        beds: '',
        baths: '',
        sqft: '',
        description: '',
        mls: '',
        listingAgent: '',
        status: 'for sale'
    });

    const [existingImages, setExistingImages] = useState([]); // {id, filename, url}
    const [removedImages, setRemovedImages] = useState([]);
    const [newImages, setNewImages] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isEditMode) {
            axios.get(`https://amywagnerdesigns.com/api/real-estate-listings/${id}`)
                .then(res => {
                    setListing(res.data);
                    setExistingImages(res.data.images || []);
                })
                .catch(err => console.error("Error loading listing:", err));
        }
    }, [id, isEditMode]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setListing(prev => ({ ...prev, [name]: value }));
    };

    const onDrop = useCallback((acceptedFiles) => {
        setNewImages(prev => [...prev, ...acceptedFiles]);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: 'image/*',
        multiple: true
    });

    const handleRemoveExistingImage = (index) => {
        const img = existingImages[index];
        setRemovedImages(prev => [...prev, img.id]);
        setExistingImages(prev => prev.filter((_, i) => i !== index));
    };

    const handleRemoveNewImage = (index) => {
        setNewImages(prev => prev.filter((_, i) => i !== index));
    };

    const handleSaveListing = async () => {
        const formData = new FormData();
        Object.entries(listing).forEach(([key, value]) => {
            formData.append(key, value);
        });

        newImages.forEach(file => formData.append('images', file));

        if (removedImages.length > 0) {
            formData.append('removedImages', JSON.stringify(removedImages));
        }

        setLoading(true);
        try {
            if (isEditMode) {
                await axios.put(`https://amywagnerdesigns.com/api/real-estate-listings/${id}`, formData);
            } else {
                await axios.post(`https://amywagnerdesigns.com/api/real-estate-listings`, formData);
            }
            navigate(`/realestateservices`);
        } catch (err) {
            console.error("Error saving listing:", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="edit-listing-page">
            <h1>{isEditMode ? "Edit Listing" : "Create New Listing"}</h1>
            <div className="listing-form">
                {[
                    "address", "city", "state", "zip", "price",
                    "beds", "baths", "sqft", "mls", "listingAgent"
                ].map(field => (
                    <input key={field} type="text" name={field} placeholder={field} value={listing[field]} onChange={handleInputChange} />
                ))}

                <textarea name="description" placeholder="Description" value={listing.description} onChange={handleInputChange}></textarea>

                <label>Status:</label>
                <select name="status" value={listing.status} onChange={handleInputChange}>
                    <option value="for sale">For Sale</option>
                    <option value="under contract">Under Contract</option>
                    <option value="pending sale">Pending Sale</option>
                    <option value="sold">Sold</option>
                </select>

                {isEditMode && existingImages.length > 0 && (
                    <>
                        <h3>Current Images</h3>
                        <div className="image-preview">
                            {existingImages.map((img, index) => (
                                <div key={img.id} className="image-item">
                                    <img src={img.url} alt={`Image ${index}`} />
                                    <button onClick={() => handleRemoveExistingImage(index)}>Remove</button>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                <div {...getRootProps()} className="dropzone">
                    <input {...getInputProps()} />
                    {isDragActive ? <p>Drop images here...</p> : <p>Drag & drop or click to upload</p>}
                </div>

                {newImages.length > 0 && (
                    <>
                        <h3>New Images</h3>
                        <div className="image-preview">
                            {newImages.map((file, index) => (
                                <div key={index} className="image-item">
                                    <img src={URL.createObjectURL(file)} alt={`New ${index}`} />
                                    <button onClick={() => handleRemoveNewImage(index)}>Remove</button>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                <button className="save-listing-btn" onClick={handleSaveListing} disabled={loading}>
                    {loading ? "Saving..." : isEditMode ? "Update Listing" : "Create Listing"}
                </button>
            </div>
        </div>
    );
};

export default CreateEditListing;
